import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {volcanoAlertLevels,philippineVolcanoes} from '../data/volcanoes';

const {FiAlertTriangle,FiActivity,FiInfo,FiMapPin}=FiIcons;

const VolcanoLegend=()=> {
  const activeVolcanoes=philippineVolcanoes.filter(v=> v.alertLevel > 0);
  const criticalVolcanoes=philippineVolcanoes.filter(v=> v.alertLevel >=3);

  const AlertLevelIndicator=({level,config})=> (
    <div className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
      <div className="flex items-center space-x-3">
        <div className="w-4 h-4 rounded-full border-2 border-white shadow-sm" style={{backgroundColor: config.color}} />
        <div>
          <div className="font-medium text-sm text-gray-900">
            Level {level}: {config.name}
          </div>
          <div className="text-xs text-gray-600">
            {config.description}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{opacity: 0,y: 20}}
      animate={{opacity: 1,y: 0}}
      className="bg-white rounded-lg shadow-md p-4 border border-gray-200"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiActivity} className="text-orange-600" />
          <h3 className="font-semibold text-gray-900">Volcanic Activity</h3>
        </div>
        <div className="flex items-center space-x-2 text-sm">
          <SafeIcon icon={FiMapPin} className="text-gray-500" />
          <span className="text-gray-600">{philippineVolcanoes.length} monitored</span>
        </div>
      </div>

      {/* Alert Status Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <SafeIcon icon={FiAlertTriangle} className="text-orange-600" />
            <span className="font-medium text-sm text-orange-900">Active Volcanoes</span>
          </div>
          <div className="text-2xl font-bold text-orange-600">{activeVolcanoes.length}</div>
          <div className="text-xs text-orange-700">Showing abnormal activity</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <SafeIcon icon={FiAlertTriangle} className="text-red-600" />
            <span className="font-medium text-sm text-red-900">Critical Status</span>
          </div>
          <div className="text-2xl font-bold text-red-600">{criticalVolcanoes.length}</div>
          <div className="text-xs text-red-700">Elevated alert levels</div>
        </div>
      </div>

      {/* Alert Levels */}
      <div className="space-y-2 mb-4">
        <h4 className="font-medium text-sm text-gray-700 mb-2">Alert Levels</h4>
        {Object.entries(volcanoAlertLevels).map(([level,config])=> (
          <AlertLevelIndicator key={level} level={level} config={config} />
        ))}
      </div>

      {/* Active Volcanoes List */}
      {activeVolcanoes.length > 0 && (
        <div className="border-t border-gray-200 pt-4">
          <h4 className="font-medium text-sm text-gray-700 mb-2">Currently Active Volcanoes</h4>
          <div className="space-y-2">
            {activeVolcanoes.slice(0,5).map(volcano=> {
              const alertConfig=volcanoAlertLevels[volcano.alertLevel];
              return (
                <div key={volcano.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">🌋</span>
                    <div>
                      <div className="font-medium text-sm text-gray-900">{volcano.name}</div>
                      <div className="text-xs text-gray-600">{volcano.province}</div>
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${alertConfig.bgColor} ${alertConfig.textColor}`}>
                    L{volcano.alertLevel}: {alertConfig.name}
                  </div>
                </div>
              );
            })}
            {activeVolcanoes.length > 5 && (
              <div className="text-xs text-gray-500 text-center">
                + {activeVolcanoes.length - 5} more active volcanoes
              </div>
            )}
          </div>
        </div>
      )}

      {/* Information Note */}
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start space-x-2">
          <SafeIcon icon={FiInfo} className="text-blue-600 text-xs mt-0.5 flex-shrink-0" />
          <div className="text-xs text-blue-800">
            <strong>Note:</strong> Volcanic alert levels are issued by PHIVOLCS. Level 0 (Normal) to Level 5 (Hazardous Eruption). Monitor official sources for the latest updates.
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default VolcanoLegend;